import React, { useMemo, useState } from "react";

import { InputChangeEvent } from "api/model/airflow-task/task-template/TaskTemplateInfo";
import { TaskInputGroup } from "api/model/airflow-task/TaskInputGroup";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Button } from "primereact/button";
import { Column, ColumnEditorOptions } from "primereact/column";
import { confirmPopup } from "primereact/confirmpopup";
import {
  DataTable,
  DataTableRowEditCompleteEvent,
  DataTableRowEditEvent,
  DataTableRowReorderEvent,
} from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import { v4 } from "uuid";

interface ITaskTemplatesSubgroupManagerTableProps {
  subgroups: TaskInputGroup[];
  onChange: (e: InputChangeEvent<TaskInputGroup[]>) => void;
  changeTab: (title: string) => void;
}

interface ISubgroupRowModel {
  value: TaskInputGroup | null;
  newValue: TaskInputGroup | null;
  id: string;
}

export default function TaskTemplatesSubgroupManagerTable(props: ITaskTemplatesSubgroupManagerTableProps) {
  const [subgroups, setSubgroups] = useState(() => mapToModel(props.subgroups));
  const [editingRows, setEditingRows] = useState<Record<string, boolean>>({});
  const [searchText, setSearchText] = useState("");

  const filteredSubgroups = useMemo(() => {
    return subgroups.filter(val => val.value === null || val.value?.title?.includes(searchText));
  }, [searchText, subgroups]);

  const propagateChangesStateUp = (arr: ISubgroupRowModel[]) => {
    const tmp = { value: arr.map(e => e.value) } as InputChangeEvent<TaskInputGroup[]>;
    props.onChange(tmp);
  };

  const confirmWrapper = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    confirmHandler: () => void
  ) => {
    confirmPopup({
      target: event.currentTarget,
      message: T("confirm-popup.are-you-really-want-to-delete-this-subgroup"),
      icon: "pi pi-exclamation-triangle",
      accept: confirmHandler,
    });
  };

  const showWarn = (warn: string) => {
    easasAlert({ summary: T("alert.invalid"), severity: "warn", detail: warn, life: 3000 });
  };

  const handleAdd = () => {
    const group = new TaskInputGroup({ isGrouped: true });
    const updated = { value: null, newValue: group, id: v4() } as ISubgroupRowModel;
    setSubgroups(prev => [updated, ...prev]);
    setEditingRows(prev => ({ ...prev, [updated.id]: true }));
  };

  const handleDelete = (row: ISubgroupRowModel) => {
    setSubgroups(prev => {
      const filtered = prev.filter(item => item.id !== row.id);
      propagateChangesStateUp(filtered);
      return filtered;
    });
  };

  const handleRowEditInit = (e: DataTableRowEditEvent) => {
    const data = e.data as ISubgroupRowModel;
    data.newValue = { ...data.value! };
  };

  const handleRowEditCancel = (e: DataTableRowEditEvent) => {
    const data = e.data as ISubgroupRowModel;
    if (!data.newValue?.title || !data.value?.title) {
      setSubgroups(prev => prev.filter(item => item.id !== data.id));
      setEditingRows(prev => ({ ...prev, [data.id]: false }));
    }
    data.newValue = null;
  };

  const checkForDuplicates = (newData: ISubgroupRowModel) => {
    const duplicates = subgroups.filter(
      it => it.value?.title === newData?.newValue?.title && it.id !== newData?.id
    ).length;
    return duplicates > 0;
  };

  const handleRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    const newData = e.newData as ISubgroupRowModel;
    if (!newData?.newValue?.title || checkForDuplicates(newData)) {
      return;
    }

    setSubgroups(prev => {
      const updated = prev.map((item, index) =>
        index === e.index ? { ...item, value: newData.newValue } : item
      );
      propagateChangesStateUp(updated);
      return updated;
    });
  };

  const onRowEditChange = (e: DataTableRowEditEvent) => {
    const newData = subgroups.at(e.index);
    const exist = e.data[newData!.id];
    if (!exist) {
      if (!newData?.newValue?.title === null) {
        showWarn(T("alert.subgroup-title-is-required"));
        return;
      }
      if (checkForDuplicates(newData!)) {
        showWarn(T("alert.title-is-already-used"));
        return;
      }
    }
    setEditingRows(e.data);
  };

  const onRowReorder = (e: DataTableRowReorderEvent<ISubgroupRowModel[]>) => {
    const reorderedRows = [...subgroups];
    const draggedRow = reorderedRows[e.dragIndex];

    reorderedRows.splice(e.dragIndex, 1);
    reorderedRows.splice(e.dropIndex, 0, draggedRow);

    setSubgroups(reorderedRows);
    propagateChangesStateUp(reorderedRows);
  };

  const titleTemplate = (
    <div className="flex justify-content-between align-items-center">
      <div className="flex flex-row align-items-center gap-3">
        <div className="font-bold" style={{ fontSize: "18px" }}>
          {T("word.subgroups")}
        </div>
        <InputText
          placeholder={T("action.search")}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
        />
      </div>
      <Button
        icon="pi pi-plus"
        label={T("action.add")}
        className="component-editor-button icon-green"
        onClick={handleAdd}
      />
    </div>
  );
  const keyHandle = (e: React.KeyboardEvent<HTMLInputElement>, opt: ColumnEditorOptions) => {
    if (e.code === "Enter" || e.code === "NumpadEnter") {
      e.preventDefault();
      handleRowEditComplete({ newData: opt.rowData, index: opt.rowIndex } as DataTableRowEditCompleteEvent);
      const next = { ...editingRows };
      delete next[opt.rowData.id];
      onRowEditChange({ index: opt.rowIndex, data: next } as DataTableRowEditEvent);
    }
    if (e.key === "Escape") {
      e.preventDefault();
      handleRowEditCancel({ data: opt.rowData } as DataTableRowEditEvent);
      setEditingRows({});
    }
  };

  const titleEditorTemplate = (opt: ColumnEditorOptions) => (
    <InputText
      placeholder={T("word.title")}
      value={opt.rowData.newValue.title || ""}
      onChange={e => opt.editorCallback?.(e.target.value)}
      onKeyDown={e => keyHandle(e, opt)}
    />
  );

  const changeTab = (title?: string) => {
    title && props.changeTab(title);
  };

  const actionBodyTemplate = (row: ISubgroupRowModel) => {
    return (
      <>
        <Button
          icon="pi pi-trash"
          className="const-del-btn  p-button-rounded p-button-text p-button-danger"
          onClick={e => confirmWrapper(e, () => handleDelete(row))}
        />
        <Button
          icon="pi pi-reply"
          className="p-button-rounded p-button-text"
          onClick={() => changeTab(row.value?.title)}
        />
      </>
    );
  };

  return (
    <DataTable
      value={filteredSubgroups}
      header={titleTemplate}
      width="100%"
      dataKey="id"
      editMode="row"
      onRowEditInit={handleRowEditInit}
      onRowEditCancel={handleRowEditCancel}
      onRowEditComplete={handleRowEditComplete}
      onRowEditChange={onRowEditChange}
      onRowReorder={onRowReorder}
      editingRows={editingRows}
      size="small"
      reorderableRows
      paginator={searchText.length === 0}
      paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
      currentPageReportTemplate={`${T("word.showing")} {first} to {last} of {totalRecords}`}
      rows={7}
    >
      <Column rowReorder style={{ width: "4em" }} />
      <Column
        field="newValue.title"
        header={T("word.title")}
        editor={titleEditorTemplate}
        body={row => row.value?.title}
      />
      <Column rowEditor align="right" className="w-5rem p-0" />
      <Column body={actionBodyTemplate} className="w-6rem p-0" />
    </DataTable>
  );
}

const mapToModel = (data: any[]) => {
  return data.map(it => {
    return { value: it, id: v4() } as ISubgroupRowModel;
  });
};
